import React from 'react';
import { Info, Save, RefreshCw } from 'lucide-react';
import type { FlatBrandNode } from '../../../utils/brand';
import type {
  ProductOptionGroupFormData,
  ProductOptionGroupFormErrors,
} from './productOptionGroupFormTypes';

const FormRow: React.FC<{
  label: string;
  required?: boolean;
  children: React.ReactNode;
  error?: string;
}> = ({ label, required, children, error }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 border-b border-gray-100 last:border-0">
    <div className="w-52 sm:w-56 flex-shrink-0">
      <p className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </p>
    </div>
    <div className="flex-1 min-w-0">
      {children}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  </div>
);

const OptionButtons: React.FC<{
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}> = ({ options, value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((opt) => {
      const selected = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            selected ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <span className={`w-2 h-2 rounded-full ${selected ? 'bg-white' : 'bg-gray-400'}`} />
          {opt.label}
        </button>
      );
    })}
  </div>
);

const BOOL_OPTIONS = [
  { value: 'true', label: '예' },
  { value: 'false', label: '아니오' },
];

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: '활성' },
  { value: 'INACTIVE', label: '비활성' },
  { value: 'PENDING', label: '대기중' },
];

export interface ProductOptionGroupInfoTabProps {
  mode: 'create' | 'update';
  formData: ProductOptionGroupFormData;
  setFormData: React.Dispatch<React.SetStateAction<ProductOptionGroupFormData>>;
  errors: ProductOptionGroupFormErrors;
  setErrors: React.Dispatch<React.SetStateAction<ProductOptionGroupFormErrors>>;
  brands: FlatBrandNode[];
  onSubmit: () => Promise<void>;
  onReset: () => void;
  isSubmitting: boolean;
  submitButtonText: string;
  codeReadOnly?: boolean;
  onCancel?: () => void;
}

const ProductOptionGroupInfoTab: React.FC<ProductOptionGroupInfoTabProps> = ({
  mode,
  formData,
  setFormData,
  errors,
  setErrors,
  brands,
  onSubmit,
  onReset,
  isSubmitting,
  submitButtonText,
  codeReadOnly,
  onCancel,
}) => {
  const setNum = (name: keyof ProductOptionGroupFormData, v: number) => {
    setFormData((prev) => ({ ...prev, [name]: v }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name as keyof ProductOptionGroupFormErrors];
      return next;
    });
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 lg:px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Info className="w-5 h-5 mr-2" />
            기본 정보
          </h2>
        </div>
        <div className="px-4 lg:px-6 py-2">
          <FormRow label="브랜드" required error={errors.brandCode}>
            <select
              value={formData.brandCode}
              disabled={mode === 'update'}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, brandCode: e.target.value }));
                setErrors((prev) => {
                  const n = { ...prev };
                  delete n.brandCode;
                  return n;
                });
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm ${
                errors.brandCode ? 'border-red-500' : 'border-gray-300'
              } ${mode === 'update' ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
            >
              <option value="">브랜드 선택</option>
              {brands.map((b) => (
                <option key={b.code} value={b.code}>
                  {b.name} ({b.code})
                </option>
              ))}
            </select>
            {mode === 'update' && (
              <p className="mt-1 text-xs text-gray-500">브랜드는 수정 시 변경할 수 없습니다.</p>
            )}
          </FormRow>

          <FormRow label="그룹 코드" required error={errors.code}>
            <input
              type="text"
              value={formData.code}
              readOnly={codeReadOnly}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  code: e.target.value.replace(/\s/g, ''),
                }))
              }
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm ${
                codeReadOnly ? 'bg-gray-100 text-gray-600' : 'border-gray-300'
              }`}
              placeholder="영문·숫자 등"
            />
          </FormRow>

          <FormRow label="그룹 상태" required error={errors.status}>
            <OptionButtons
              options={STATUS_OPTIONS}
              value={formData.status}
              onChange={(v) => setFormData((prev) => ({ ...prev, status: v }))}
            />
          </FormRow>

          <FormRow label="그룹 명칭" required error={errors.name}>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="표시 이름"
            />
          </FormRow>

          <FormRow label="필수 여부">
            <OptionButtons
              options={BOOL_OPTIONS}
              value={formData.required ? 'true' : 'false'}
              onChange={(v) =>
                setFormData((prev) => ({ ...prev, required: v === 'true' }))
              }
            />
          </FormRow>

          <FormRow label="다중 선택">
            <OptionButtons
              options={BOOL_OPTIONS}
              value={formData.multiSelect ? 'true' : 'false'}
              onChange={(v) => {
                const nextMultiSelect = v === 'true';
                setFormData((prev) => ({
                  ...prev,
                  multiSelect: nextMultiSelect,
                  minSelectOptions: nextMultiSelect ? Math.max(1, prev.minSelectOptions) : 1,
                  maxSelectOptions: nextMultiSelect ? Math.max(1, prev.maxSelectOptions) : 1,
                }));
              }}
            />
          </FormRow>

          <FormRow label="최소 선택 옵션 수" error={errors.minSelectOptions}>
            <input
              type="number"
              min={1}
              value={formData.minSelectOptions}
              onChange={(e) => {
                const next = Number(e.target.value);
                setNum('minSelectOptions', Math.max(1, next));
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </FormRow>

          <FormRow label="최대 선택 옵션 수" error={errors.maxSelectOptions}>
            <input
              type="number"
              min={1}
              value={formData.maxSelectOptions}
              disabled={!formData.multiSelect}
              onChange={(e) => {
                const next = Number(e.target.value);
                if (!formData.multiSelect) {
                  setNum('maxSelectOptions', 1);
                  return;
                }
                setNum('maxSelectOptions', Math.max(1, next));
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm ${
                formData.multiSelect
                  ? 'border-gray-300'
                  : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            />
          </FormRow>

          <FormRow label="최소 옵션 수량 합" error={errors.minOptionQtySum}>
            <input
              type="number"
              min={0}
              value={formData.minOptionQtySum}
              onChange={(e) => setNum('minOptionQtySum', Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </FormRow>

          <FormRow label="최대 옵션 수량 합" error={errors.maxOptionQtySum}>
            <input
              type="number"
              min={0}
              value={formData.maxOptionQtySum}
              onChange={(e) => setNum('maxOptionQtySum', Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </FormRow>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-6">
        <button
          type="button"
          onClick={onReset}
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw className="w-4 h-4" />
          초기화
        </button>
        <div className="flex flex-wrap gap-2 justify-end">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              취소
            </button>
          )}
          <button
            type="button"
            onClick={() => onSubmit()}
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {submitButtonText}
          </button>
        </div>
      </div>
    </>
  );
};

export default ProductOptionGroupInfoTab;